import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { ICommentariesDataItem, ICommentariesLayout } from '../../types/layout/commentaries';
import useRest from '../useRest';
import { commentariesListSelector } from '../../redux/selectors/commentaries';
import { setData } from '../../redux/actions/commentaries';
import { ICommentariesProps } from '../../components/commentaries';

export const useCommentaries = ({
    dataIndex, dataLink, uploadLink, sendAction, rowId,
}: ICommentariesLayout & ICommentariesProps) => {
    const dispatch = useDispatch();
    const list = useSelector(commentariesListSelector(dataIndex));
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState<string>();
    const [sendLoading, setSendLoading] = useState(false);

    const { objectId } = useParams();

    const rest = useRest();

    const updateLink = (link: string) => {
        if (typeof objectId === 'string') {
            return link.replace('{objectId}', objectId);
        }
        if (typeof rowId === 'string' || typeof rowId === 'number') {
            return link.replace('{objectId}', rowId.toString());
        }
        return link;
    };

    const getList = async () => {
        setLoading(true);

        const result = await rest.fetch({
            method: 'post',
            objectName: updateLink(dataLink),
        });

        if (!result.isError && Array.isArray(result.response?.data)) {
            dispatch(setData(dataIndex, result.response?.data as ICommentariesDataItem[]));
        }

        setLoading(false);
    };

    const onSend = async () => {
        setSendLoading(true);

        const result = await rest.fetch({
            method: 'post',
            objectName: updateLink(sendAction),
            data: {
                value,
            },
        });

        if (!result.isError) {
            getList();
            setValue('');
        } else {
            Swal.fire({
                toast: true,
                title: 'Ошибка запроса',
                text: result.response?.data?.errorText || result.err || 'Непредвиденная ошибка',
                icon: 'error',
                position: 'top-right',
                showConfirmButton: false,
                timer: 3000,
            });
        }

        setSendLoading(false);
    };

    const uploadFile = async (files: File[]) => {
        if (typeof uploadLink === 'string') {
            setLoading(true);

            const formData = new FormData();

            files.forEach((file) => {
                formData.append('files[]', file);
            });

            const result = await rest.fetch({
                method: 'post',
                objectName: updateLink(uploadLink),
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!result.isError) {
                getList();
            } else {
                Swal.fire({
                    toast: true,
                    title: 'Ошибка запроса',
                    text: result.response?.data?.errorText || result.err || 'Непредвиденная ошибка',
                    icon: 'error',
                    position: 'top-right',
                    showConfirmButton: false,
                    timer: 3000,
                });
            }

            setLoading(false);
        }
    };

    useEffect(() => {
        getList();
        return () => {
            dispatch(setData(dataIndex, []));
        };
    }, [dataLink]);

    return {
        list,
        loading,
        value,
        setValue,
        onSend,
        sendLoading,
        uploadFile,
    };
};
